import { useState } from "react";
import { motion } from "framer-motion";
import { Container } from "@/components/ui/Container";
import { fadeInUp } from "@/lib/motion";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/layout/PageHeader";
import { useLanguage } from "@/components/LanguageContext";
import { SEO } from "@/components/layout/SEO";

export default function Solutions() {
  const { t } = useLanguage();
  const [activeModal, setActiveModal] = useState<number | null>(null);

  const solutions = [
    {
      title: t("Diagnostics Territoriaux", "Territorial Diagnostics"),
      tagline: t("Comprendre la base", "Understand the baseline"),
      description: t("Analyse spatiale multi-critères et collecte de données pour donner aux décideurs une compréhension claire et quantitative des dynamiques de mobilité actuelles, des lacunes en infrastructure et des impacts environnementaux.", "Multi-criteria spatial analysis and data collection to give decision-makers a clear, quantitative understanding of current mobility dynamics, infrastructure gaps, and environmental impacts."),
      image: "/images/solutions/heatmap-marrakech.jpg"
    },
    {
      title: t("Modélisation Trafic & Transport", "Traffic & Transport Modeling"),
      tagline: t("Prévoir l'avenir", "Forecast the future"),
      description: t("Simulations macroscopiques et mésoscopiques des réseaux de transport pour évaluer l'impact des nouvelles infrastructures, des changements politiques ou des évolutions démographiques sur les flux de trafic.", "Macroscopic and mesoscopic simulations of transport networks to evaluate the impact of new infrastructure, policy shifts, or demographic changes on traffic flows."),
      image: "/images/solutions/etude-autoroutier.jpg"
    },
    {
      title: t("Stratégie ZFE", "Low-Emission Zone (ZFE) Strategy"),
      tagline: t("Conduire le développement durable", "Drive sustainability"),
      description: t("Conseil de bout en bout pour la mise en œuvre de ZFE : du calcul de la base des émissions et de la définition du périmètre aux évaluations de l'impact socio-économique.", "End-to-end consulting for the implementation of ZFEs: from emissions baseline calculation and perimeter definition to socio-economic impact assessments."),
      image: "/images/solutions/zfe-marrakech.jpg"
    },
    {
      title: t("Systèmes de Transport Intelligents (STI)", "Intelligent Transport Systems (ITS)"),
      tagline: t("Optimiser les opérations", "Optimize operations"),
      description: t("Stratégies de conseil et déploiement pour l'infrastructure numérique, la signalisation intelligente et les centres de gestion du trafic en temps réel.", "Advisory and deployment strategies for digital infrastructure, smart signaling, and real-time traffic management centers."),
      image: "/images/solutions/etude-sti.jpg"
    }
  ];

  return (
    <>
      <SEO 
        title={t("Nos Solutions", "Our Solutions")}
        description={t(
          "Découvrez nos solutions stratégiques pour la transition urbaine : diagnostics territoriaux, modélisation de transport et zones à faibles émissions (ZFE).",
          "Discover our strategic solutions for urban transition: territorial diagnostics, transport modeling, and low-emission zones (ZFE)."
        )}
      />
      <PageHeader 
        overlineFr="Nos Solutions"
        overlineEn="Our Solutions"
        titleFr="Des Stratégies Actionnables pour la Transformation Urbaine."
        titleEn="Actionable Strategies for Urban Transformation."
        descriptionFr="Nous regroupons notre expertise multidisciplinaire en solutions complètes qui répondent aux défis les plus urgents des villes modernes."
        descriptionEn="We package our multidisciplinary expertise into comprehensive solutions that address the most pressing challenges of modern cities."
        animatedGif={true}
      />

      <section className="py-20 lg:py-32 bg-warm-white">
        <Container>
          <div className="space-y-20">
            {solutions.map((sol, i) => (
              <motion.div 
                key={i} 
                className={`flex flex-col ${i % 2 !== 0 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-20 items-center`}
                variants={fadeInUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
              >
                <div className="w-full lg:w-1/2">
                  <div className="w-full aspect-[4/3] bg-mineral rounded-2xl relative overflow-hidden flex items-center justify-center shadow-sm">
                     <img 
                       src={sol.image} 
                       alt={sol.title} 
                       className="w-full h-full object-cover"
                     />
                     <div className="absolute inset-0 border border-charcoal/5 rounded-2xl m-4 pointer-events-none" />
                  </div>
                </div>
                <div className="w-full lg:w-1/2 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-amber font-semibold">{sol.tagline}</p>
                  <h2 className="text-3xl md:text-4xl font-display font-semibold text-charcoal">{sol.title}</h2>
                  <p className="text-base text-graphite leading-relaxed">{sol.description}</p>
                  <div className="pt-4">
                    <Button variant="ghost" className="px-0" onClick={() => setActiveModal(i)}>
                      {t("En Savoir Plus", "Learn More")} &rarr;
                    </Button>
                  </div>
                  <Modal 
                    isOpen={activeModal === i} 
                    onClose={() => setActiveModal(null)} 
                    title={sol.title} 
                    description={sol.description} 
                    tagline={sol.tagline} 
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>
    </>
  );
}
